import { Play, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';

interface QueryTesterProps {
  query: string;
  queryParams: Record<string, string | number | boolean>;
  isTesting: boolean;
  onTest: (args: {
    query: string;
    queryParams: Record<string, string | number | boolean>;
  }) => Promise<{
    valid: boolean;
    resultCount?: number;
    sampleResult?: any;
    message?: string;
  }>;
}

export function QueryTester({
  query,
  queryParams,
  isTesting,
  onTest,
}: QueryTesterProps) {
  const [testResult, setTestResult] = useState<string | null>(null);

  const handleTest = async () => {
    if (!query.trim()) {
      toast.error('Please enter a query before testing.');
      return;
    }

    try {
      const result = await onTest({
        query,
        queryParams,
      });

      if (result.valid) {
        let resultText = `✅ Valid query!`;

        if (result.resultCount !== undefined) {
          resultText += ` Returned ${result.resultCount} result(s).`;
        }

        if (result.sampleResult) {
          try {
            const parsed =
              typeof result.sampleResult === 'string'
                ? JSON.parse(result.sampleResult)
                : result.sampleResult;

            if (Array.isArray(parsed)) {
              const shown = parsed.slice(0, 3);
              resultText += `\n\n📋 First ${shown.length} result(s):\n${JSON.stringify(shown, null, 2)}`;
              if (parsed.length > 3) {
                resultText += `\n\n... and ${parsed.length - 3} more`;
              }
            } else {
              resultText += `\n\n📋 Result:\n${JSON.stringify(parsed, null, 2)}`;
            }
          } catch {
            resultText += `\n\n📋 Result:\n${result.sampleResult}`;
          }
        }

        setTestResult(resultText);
      } else {
        setTestResult(`❌ Invalid query: ${result.message}`);
      }
    } catch {
      setTestResult('❌ Error testing the query');
    }
  };

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        onClick={handleTest}
        disabled={isTesting || !query.trim()}
        className="gap-2"
      >
        {isTesting ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        ) : (
          <Play className="h-4 w-4" />
        )}
        Test Query
        {Object.keys(queryParams).length > 0 && (
          <span className="text-xs text-gray-500">
            (with {Object.keys(queryParams).length} parameter
            {Object.keys(queryParams).length !== 1 ? 's' : ''})
          </span>
        )}
      </Button>

      {testResult && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
          <div className="mb-2 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-800">
              Test Result:
            </span>
          </div>
          <div className="max-h-40 w-full overflow-auto rounded border bg-white p-2">
            <pre className="overflow-wrap-anywhere whitespace-pre-wrap break-all font-mono text-xs text-green-700">
              {testResult}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
